/**
 * Cross-subdomain storage utilities
 * Uses cookies to share data across subdomains (ai.ilouli.com, community.ilouli.com, etc.)
 */

/**
 * Get the root domain for cookie sharing
 * @returns {string} Root domain (e.g., ".ilouli.com" or "" for localhost)
 */
const getRootDomain = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return ''; // No domain restriction for localhost
  }
  // Extract root domain (e.g., "ilouli.com" from "ai.ilouli.com")
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    return '.' + parts.slice(-2).join('.');
  }
  return '';
};

/**
 * Set a cross-subdomain cookie
 * @param {string} name - Cookie name
 * @param {*} value - Value to store (will be JSON stringified)
 * @param {number} days - Expiration in days (default: 1)
 */
export const setCrossDomainCookie = (name, value, days = 1) => {
  try {
    const jsonValue = JSON.stringify(value);
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

    const domain = getRootDomain();
    const domainPart = domain ? `; domain=${domain}` : '';
    const securePart = window.location.protocol === 'https:' ? '; secure' : '';

    document.cookie = `${name}=${encodeURIComponent(jsonValue)}; expires=${expires.toUTCString()}; path=/${domainPart}${securePart}; SameSite=Lax`;
  } catch (e) {
    console.error('Failed to set cross-domain cookie:', e);
  }
};

/**
 * Get a cross-subdomain cookie
 * @param {string} name - Cookie name
 * @returns {*} Parsed value or null
 */
export const getCrossDomainCookie = (name) => {
  try {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [cookieName, cookieValue] = cookie.trim().split('=');
      if (cookieName === name && cookieValue) {
        return JSON.parse(decodeURIComponent(cookieValue));
      }
    }
    return null;
  } catch (e) {
    console.error('Failed to get cross-domain cookie:', e);
    return null;
  }
};

/**
 * Remove a cross-subdomain cookie
 * @param {string} name - Cookie name
 */
export const removeCrossDomainCookie = (name) => {
  try {
    const domain = getRootDomain();
    const domainPart = domain ? `; domain=${domain}` : '';

    // Remove cookie by setting expired date
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainPart}`;
  } catch (e) {
    console.error('Failed to remove cross-domain cookie:', e);
  }
};

// Cookie key for audio analysis
export const CROSS_DOMAIN_KEYS = {
  AUDIO_ANALYSIS_PENDING: 'ilouli_audio_pending',
};
